import React, { useCallback, useState } from 'react';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { QueryKey } from 'api/queryKey';
import { useQuery, useQueryCache } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Header } from 'common/components/Header';
import Column from 'antd/lib/table/Column';
import { Link } from 'react-router-dom';
import { useEnsureContext } from 'common/hooks';
import { UserStatus } from 'models/userStatus';
import { AssignPartnerDeviceToUserButton } from 'common/components/AssignPartnerDeviceToUserModal';
import { BusinessPartnerProfileOwnerManagementRoutes } from 'pages/BusinessPartnerManagement/BusinessPartnerProfileOwnersManagement/businessPartnerProfileOwnerManagementRoutes';
import { useRouteMatch } from 'react-router';
import { UnassignUserPartnerDeviceButton } from 'common/components/UnassignUserPartnerDevice';
import { ProfileOwnersFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerProfileOwnersManagement/BusinessPartnerProfileOwnersPage/ProfileOwnersFilters';
import { ProfileOwner, ProfileOwnerDTO } from 'models/user';
import { Button, Dropdown, Menu, message } from 'antd';
import { UserStatusTag } from 'common/components/UserStatusTag';
import { purge } from 'lib/ramda/purge';
import { DeleteUserStatus } from 'models/deleteUserStatus';
import { DeleteOutlined, RedoOutlined, SwapOutlined } from '@ant-design/icons';
import { BusinessPartnerDeleteProfileOwnerButton } from 'pages/BusinessPartnerManagement/BusinessPartnerProfileOwnersManagement/BusinessPartnerProfileOwnersPage/BusinessPartnerDeleteProfileOwnerButton';
import { RequestState, useRequestsState } from 'common/hooks/useRequestsState';
import { useRole } from 'common/hooks/useRole';
import { AdministrationGuard } from 'common/components/AdministrationGuard';
import { ProfileOwnerTransferModal } from 'pages/BusinessPartnerManagement/BusinessPartnerProfileOwnersManagement/BusinessPartnerProfileOwnersPage/ProfileOwnerTransferModal';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';
import { NumberedTable } from 'common/components/NumberedTable';
import { toSimpleDate } from 'common/dateFnsUtils';
import { UsersActivateButton } from 'pages/BusinessPartnerManagement/UsersActivateButton';
import { makeSubpartnersIdsString } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/utils/makeSubpartnersIDsString';
import { isNil } from 'ramda';

export const BusinessPartnerUsersPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { url } = useRouteMatch();
  const queryCache = useQueryCache();
  const { isAdministrationRole } = useRole();
  const { isSuperAdmin } = useRole();
  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState({});
  const [filteredPartnerId, setFilteredPartnerId] = useState(0);
  const [transferModalState, setTransferModalState] = useState<{
    visible: boolean;
    user?: ProfileOwnerDTO;
  }>({
    visible: false,
  });

  const IDlist: number[] = [];
  const [IDsToGetData, setIDsToGetData] = useState('');

  const invalidateProfileOwners = useCallback(
    () =>
      queryCache
        .invalidateQueries(QueryKey.BusinessPartnerProfileOwnersList)
        .then(),
    [queryCache]
  );

  const {
    isLoading: isLoadingSubpartners,
    isSuccess: isSubpartnersSuccess,
  } = useQuery(
    [QueryKey.BusinessPartnersList],
    () => BusinessPartnersAPI.fetchSubpartners(bpContext.id),
    {
      onSuccess: (res) => {
        invalidateProfileOwners();
        const IDsString = makeSubpartnersIdsString(res, IDlist);
        setIDsToGetData(IDsString);
      },
      keepPreviousData: true,
    }
  );

  const { isFetching, data } = useQuery(
    [
      QueryKey.BusinessPartnerProfileOwnersList,
      pageMetaData,
      filters,
      IDsToGetData,
    ],
    () =>
      BusinessPartnersAPI.fetchProfileOwners(
        filteredPartnerId !== 0 ? filteredPartnerId : bpContext.id,
        {
          subPartnersIds: filteredPartnerId !== 0 ? '' : IDsToGetData,
          ...pageMetaData,
          ...filters,
        }
      ),
    {
      enabled: isSubpartnersSuccess,
      keepPreviousData: true,
    }
  );

  const intlMessage = useFormatMessage();
  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  const [state, resetPassword] = useRequestsState((po: ProfileOwnerDTO) =>
    BusinessPartnersAPI.resetProfileOwnerPassword({
      businessPartnerId: bpContext.id,
      id: po.userId!,
    }).then(() => {
      message.success(
        intlMessage(
          { id: 'person.passwordReset.success' },
          {
            name: `${po.firstName} ${po.lastName}`,
          }
        )
      );
      queryCache.invalidateQueries(QueryKey.BusinessPartnerProfileOwnersList);
    })
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const hideTransferModal = () => {
    setTransferModalState({ visible: false });
  };

  return (
    <>
      {transferModalState.user && (
        <ProfileOwnerTransferModal
          visible={transferModalState.visible}
          hideModal={hideTransferModal}
          user={transferModalState.user}
        />
      )}

      <Header
        title={intlMessage({ id: 'familyUsers' })}
        extra={[
          <AdministrationGuard
            key={`${url}/${BusinessPartnerProfileOwnerManagementRoutes.Register}`}
          >
            <Link
              id="button-register-family-user"
              className="ant-btn ant-btn-primary"
              key={`${url}/${BusinessPartnerProfileOwnerManagementRoutes.Register}`}
              to={`${url}/${BusinessPartnerProfileOwnerManagementRoutes.Register}`}
            >
              <FormattedMessage id="familyUsers.register" />
            </Link>
          </AdministrationGuard>,
        ]}
      />

      <ProfileOwnersFilters
        shouldHidePartnerColumn={shouldHidePartnerColumn}
        style={{ marginBottom: '1rem' }}
        onSubmit={(values) => {
          (values.partnerID === 0 || !values.partnerID) &&
            setFilteredPartnerId(0);
          values.partnerID && setFilteredPartnerId(values.partnerID);
          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
          setFilters(purge(values));
        }}
      />

      <NumberedTable
        rowKey="id"
        loading={isLoadingSubpartners || isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
        scroll={{ x: 'auto' }}
      >
        {!isLoadingSubpartners && data?.content && (
          <>
            <Column
              title={intlMessage({ id: 'name' })}
              dataIndex="firstName"
              render={(value, record: ProfileOwnerDTO) => (
                <Link
                  key={`${url}/${record.userId}`}
                  to={`${url}/${record.userId}`}
                >
                  {record.firstName} {record.lastName}
                </Link>
              )}
            />
            <Column title="Email/Login" dataIndex="email" />
            {!shouldHidePartnerColumn && (
              <Column
                title={intlMessage({ id: 'partner' })}
                dataIndex={['partnerDetails', 'name']}
              />
            )}
            <Column
              title={intlMessage({ id: 'create_date' })}
              dataIndex="createDate"
              render={(value) => toSimpleDate(value)}
            />
            <Column
              title={intlMessage({ id: 'familyUsers.lastLogin' })}
              dataIndex="lastLoginDate"
              render={(value) => (value === null ? '--' : toSimpleDate(value))}
              align="center"
              width={150}
            />

            <Column
              title={intlMessage({ id: 'status' })}
              dataIndex="status"
              render={(value) => <UserStatusTag status={value} />}
            />
            <Column
              title={intlMessage({ id: 'device' })}
              key="device"
              align="right"
              render={(value, record: ProfileOwner) =>
                record.assignedDevice
                  ? record.assignedDevice?.serialNumber
                  : 'n/a'
              }
            />
            {isAdministrationRole && (
              <Column
                title={intlMessage({ id: 'actions' })}
                key="actions"
                align="right"
                render={(value, record: ProfileOwner) => {
                  const notArchived = record.status !== UserStatus.Archived;
                  const notDeactivated =
                    record.status !== UserStatus.Deactivated;
                  const notDeleted = record.status !== UserStatus.Deleted;
                  const isDeactivated =
                    record.status === UserStatus.Deactivated;

                  return (
                    <Dropdown.Button
                      overlay={
                        <Menu>
                          {notArchived &&
                            notDeactivated &&
                            !record.assignedDevice && (
                              <Menu.Item
                                key="assign"
                                id="menu-item-assign-device-profile-owner"
                              >
                                <AssignPartnerDeviceToUserButton
                                  type="text"
                                  tooltip={null}
                                  businessPartnerId={
                                    record.partnerDetails?.id || bpContext.id
                                  }
                                  businessPartnerName={
                                    record.partnerDetails?.name ||
                                    bpContext.name
                                  }
                                  user={record}
                                  onAssign={invalidateProfileOwners}
                                >
                                  <FormattedMessage
                                    id="devices.assign"
                                    tagName="span"
                                  />
                                </AssignPartnerDeviceToUserButton>
                              </Menu.Item>
                            )}
                          {record.assignedDevice && notDeactivated && (
                            <Menu.Item
                              key="unassign"
                              id="menu-item-unassign-device-profile-owner"
                            >
                              <UnassignUserPartnerDeviceButton
                                type="text"
                                tooltip={null}
                                businessPartnerId={bpContext.id}
                                device={record.assignedDevice}
                                user={record}
                                onUnassign={invalidateProfileOwners}
                              >
                                <FormattedMessage
                                  id="devices.unassign"
                                  tagName="span"
                                />
                              </UnassignUserPartnerDeviceButton>
                            </Menu.Item>
                          )}
                          {notDeleted && notDeactivated && (
                            <Menu.Item
                              key="reset-password"
                              id="menu-item-reset-password-profile-owner"
                            >
                              <Button
                                id="button-reset-profile-owner-password"
                                type="text"
                                icon={<RedoOutlined />}
                                loading={
                                  RequestState.Pending === state[record.userId!]
                                }
                                onClick={() =>
                                  resetPassword(record.userId!, record)
                                }
                              >
                                <FormattedMessage
                                  id="person.passwordReset"
                                  tagName="span"
                                />
                              </Button>
                            </Menu.Item>
                          )}
                          {isSuperAdmin && notDeactivated && (
                            <Menu.Item
                              key="transfer-user"
                              id="menu-item-transfer-user-profile-owner"
                            >
                              <Button
                                id="button-transfer-profile-owner"
                                type="text"
                                icon={<SwapOutlined />}
                                loading={
                                  RequestState.Pending === state[record.userId!]
                                }
                                onClick={() =>
                                  setTransferModalState({
                                    user: record,
                                    visible: true,
                                  })
                                }
                              >
                                <FormattedMessage
                                  id="familyUsers.transfer"
                                  tagName="span"
                                />
                              </Button>
                            </Menu.Item>
                          )}

                          {notDeactivated && notArchived && notDeleted && (
                            <Menu.Item
                              key="deactivate"
                              id="menu-item-deactivate-profile-owner"
                            >
                              <UsersActivateButton
                                actionType="deactivate"
                                userId={record.userId!}
                                type="text"
                                userName={`${record.firstName} ${record.lastName}`}
                                onStatusChange={invalidateProfileOwners}
                              >
                                <FormattedMessage
                                  id="businessPartners.deactivate"
                                  tagName="span"
                                />
                              </UsersActivateButton>
                            </Menu.Item>
                          )}
                          {isDeactivated && (
                            <Menu.Item
                              key="activate"
                              id="menu-item-activate-profile-owner"
                            >
                              <UsersActivateButton
                                actionType="activate"
                                userId={record.userId!}
                                userName={`${record.firstName} ${record.lastName}`}
                                type="text"
                                onStatusChange={invalidateProfileOwners}
                              >
                                <FormattedMessage
                                  id="businessPartners.activate"
                                  tagName="span"
                                />
                              </UsersActivateButton>
                            </Menu.Item>
                          )}
                          {notDeleted && (
                            <Menu.Item
                              key="delete"
                              id="menu-item-delete-profile-owner"
                            >
                              <BusinessPartnerDeleteProfileOwnerButton
                                type="text"
                                tooltip={null}
                                businessPartnerId={bpContext.id}
                                profileOwner={record}
                                icon={<DeleteOutlined />}
                                danger
                                status={DeleteUserStatus.Delete}
                                onDeleted={invalidateProfileOwners}
                              >
                                <FormattedMessage id="delete" tagName="span" />
                              </BusinessPartnerDeleteProfileOwnerButton>
                            </Menu.Item>
                          )}
                        </Menu>
                      }
                    />
                  );
                }}
              />
            )}
          </>
        )}
      </NumberedTable>
    </>
  );
};
